/**
 * Grouped Slot Edit — bottom-sheet editor that applies level / certification
 * changes to every slot in one scope (a task's top-level slots, or one
 * sub-team's slots) at once.
 *
 * Edit model = "one row per item, one tap = one final state". Every level and
 * every cert is a row carrying a segmented control whose LEADING segment is a
 * calm "leave as-is" anchor (כפי שהוא). A control left on its anchor is omitted
 * from the patch, so legitimate per-slot differences are preserved untouched.
 *
 * Certs use a SINGLE mutually-exclusive tri-state control (נדרשת / ניטרלי /
 * אסורה): a cert can occupy only one segment at a time, so "required AND
 * forbidden" is unrepresentable and CERT_OVERLAP can never arise from this
 * surface. Levels offer כבוי / רגיל / מוצא אחרון (off / normal / last-resort).
 *
 * The fixed footer shows a live "N of M will change" count (recomputed from the
 * same pure `validateGroupedApply` the apply path runs) and blocks Apply — with
 * an inline reason — when a slot would be left with zero levels (EMPTY_LEVELS).
 *
 * Self-contained: owns DOM + draft state, persists via config-store's batched
 * `applyGroupedSlotEdit` (single undo / single notify).
 */

import {
  type CertCtl,
  computeCertAggregate,
  computeLevelAggregate,
  type GroupedConflict,
  type GroupedSlotEdit,
  type LevelCtl,
  validateGroupedApply,
} from '../models/grouped-slot-edit';
import { Level, type SlotTemplate } from '../models/types';
import type { GroupedSlotScope } from './config-store';
import * as store from './config-store';
import { escAttr, escHtml, levelBadge } from './ui-helpers';
import { showBottomSheet, showToast } from './ui-modal';

// ─── Context injection ──────────────────────────────────────────────────────

export interface GroupedSlotEditContext {
  /** Called after a grouped edit is applied so the hosting tab re-renders. */
  rerender: () => void;
}

let _ctx: GroupedSlotEditContext | null = null;

export function initGroupedSlotEdit(ctx: GroupedSlotEditContext): void {
  _ctx = ctx;
}

// ─── State ──────────────────────────────────────────────────────────────────

const LEVEL_OPTIONS = [Level.L0, Level.L2, Level.L3, Level.L4];

/** Unified per-cert state: one segment lit at a time ⇒ overlap is impossible. */
type CertTri = 'leave' | 'required' | 'neither' | 'forbidden';

let _scope: GroupedSlotScope | null = null;
/** Slots cached at open, for live `validateGroupedApply` previews. */
let _slots: SlotTemplate[] = [];
let _handle: { close: () => void; el: HTMLElement } | null = null;

// Draft + original aggregate per control. The draft is what the user is
// composing; the original is the on-open aggregate, used to tell "this row will
// change" from "this row is left as-is". For a level the leave-anchor value is
// 'mixed'; for a cert it is 'leave'.
const _levelState = new Map<Level, LevelCtl>();
const _levelOrig = new Map<Level, LevelCtl>();
const _certState = new Map<string, CertTri>();
const _certOrig = new Map<string, CertTri>();

// ─── Segment definitions ─────────────────────────────────────────────────────

interface LevelSeg {
  val: LevelCtl;
  label: string;
  variant: string;
  warn?: boolean;
}
// Only real, final values are tappable segments. "Leave as-is" is NOT a
// segment — it is the natural result of not touching a row (indeterminate-field
// pattern: a row whose slots differ shows no selection + a quiet "מעורב" tag,
// and stays that way unless the user picks a value).
const LEVEL_SEGS: LevelSeg[] = [
  { val: 'off', label: 'כבוי', variant: 'off' },
  { val: 'normal', label: 'רגיל', variant: 'normal' },
  { val: 'low', label: 'מוצא אחרון', variant: 'low', warn: true },
];

interface CertSeg {
  val: CertTri;
  label: string;
  variant: string;
}
const CERT_SEGS: CertSeg[] = [
  { val: 'required', label: 'נדרשת', variant: 'req' },
  { val: 'neither', label: 'ניטרלי', variant: 'neither' },
  { val: 'forbidden', label: 'אסורה', variant: 'forbid' },
];

/** Map the two boolean cert aggregates onto the single tri-state at open time. */
function certTriFromAggregates(reqAgg: CertCtl, forbAgg: CertCtl): CertTri {
  if (reqAgg === 'on') return 'required';
  if (forbAgg === 'on') return 'forbidden';
  if (reqAgg === 'off' && forbAgg === 'off') return 'neither';
  return 'leave'; // any per-slot disagreement ⇒ leave each slot as-is
}

// ─── Rendering ──────────────────────────────────────────────────────────────

/**
 * Trailing slot of a row's first line:
 *  - the user CHANGED this row from its on-open state → a one-tap "↺" revert
 *    (the only way back to the original value, incl. back to a mixed/blank row);
 *  - the row's slots DIFFER and the user hasn't touched it → a quiet "מעורב"
 *    tag explaining why no segment is lit;
 *  - an untouched uniform row → nothing.
 * `mixedVal` is the row's blank/mixed sentinel ('mixed' for levels, 'leave' for
 * certs).
 */
function rowCaption(cur: string, orig: string, mixedVal: string): string {
  if (cur !== orig) {
    return '<button type="button" class="gse-revert" data-gse-revert aria-label="שחזר ערך קודם" title="שחזר ערך קודם">↺</button>';
  }
  if (cur === mixedVal) {
    return '<span class="gse-cap gse-cap-mixed" title="ערכים שונים בין המשבצות — בחירת ערך תאחד את כולן">מעורב</span>';
  }
  return '';
}

function segHtml(
  kind: 'lvl' | 'cert',
  id: string,
  val: string,
  label: string,
  active: boolean,
  variant: string,
  warn = false,
): string {
  const cls = `gse-seg gse-seg-${variant}${active ? ' gse-seg-active' : ''}`;
  const warnGlyph = warn ? '<span class="gse-seg-warn" aria-hidden="true">⚠</span>' : '';
  return `<button type="button" class="${cls}" data-gse-seg data-kind="${kind}" data-id="${escAttr(id)}" data-val="${val}" aria-pressed="${active}">${escHtml(label)}${warnGlyph}</button>`;
}

function levelRowHtml(level: Level): string {
  const st = _levelState.get(level) ?? 'mixed';
  const orig = _levelOrig.get(level) ?? 'mixed';
  const caption = rowCaption(st, orig, 'mixed');
  const segs = LEVEL_SEGS.map((s) =>
    segHtml('lvl', String(level), s.val, s.label, st === s.val, s.variant, s.warn),
  ).join('');
  return `<div class="gse-seg-row" data-gse-lvl-row="${level}">
    <div class="gse-seg-head">${levelBadge(level)}${caption}</div>
    <div class="gse-seg-control" role="group">${segs}</div>
  </div>`;
}

function certRowHtml(certId: string, label: string, color: string): string {
  const st = _certState.get(certId) ?? 'leave';
  const orig = _certOrig.get(certId) ?? 'leave';
  const caption = rowCaption(st, orig, 'leave');
  const segs = CERT_SEGS.map((s) => segHtml('cert', certId, s.val, s.label, st === s.val, s.variant)).join('');
  return `<div class="gse-seg-row" data-gse-cert-row="${escAttr(certId)}">
    <div class="gse-seg-head"><span class="gse-cert-dot" style="background:${color}"></span>${escHtml(label)}${caption}</div>
    <div class="gse-seg-control" role="group">${segs}</div>
  </div>`;
}

function sheetBody(slotCount: number): string {
  const certs = store.getCertificationDefinitions();
  const levelRows = LEVEL_OPTIONS.map(levelRowHtml).join('');
  const certRows = certs.length
    ? certs.map((c) => certRowHtml(c.id, c.label, c.color)).join('')
    : '<p class="text-muted gse-empty">אין הסמכות מוגדרות</p>';

  return `
    <p class="gse-summary">בחירת ערך תחיל אותו על כל ${slotCount} המשבצות. מה שלא תיגע בו יישאר כמו שהוא בכל משבצת.</p>
    <section class="gse-section">
      <div class="gse-section-title">דרגות</div>
      <div class="gse-section-hint">מוצא אחרון = הדרגה מותרת אך לא מועדפת</div>
      ${levelRows}
      <div class="gse-levels-warn" hidden></div>
    </section>
    <section class="gse-section">
      <div class="gse-section-title">הסמכות</div>
      ${certRows}
    </section>
    <div id="gse-error" class="gse-error" hidden></div>`;
}

function actionsHtml(): string {
  // Starts disabled: nothing has changed yet. refreshFooter() keeps it live.
  return `<button class="btn-sm btn-primary" data-gse="apply" disabled>אין שינוי</button>
    <button class="btn-sm btn-outline" data-gse="cancel">ביטול</button>`;
}

// ─── Patch building + live preview ───────────────────────────────────────────

function buildPatch(): GroupedSlotEdit {
  const levels = new Map<Level, 'off' | 'normal' | 'low'>();
  for (const [lvl, st] of _levelState) {
    if (st !== 'mixed') levels.set(lvl, st);
  }
  const requiredCerts = new Map<string, boolean>();
  const forbiddenCerts = new Map<string, boolean>();
  for (const [c, st] of _certState) {
    if (st === 'leave') continue;
    if (st === 'required') {
      requiredCerts.set(c, true);
      forbiddenCerts.set(c, false);
    } else if (st === 'forbidden') {
      requiredCerts.set(c, false);
      forbiddenCerts.set(c, true);
    } else {
      // neither: ensure the cert is on neither list
      requiredCerts.set(c, false);
      forbiddenCerts.set(c, false);
    }
  }
  return { levels, requiredCerts, forbiddenCerts };
}

/**
 * Recompute the live preview after every tap: rewrite the footer Apply button's
 * text + disabled state, and surface an inline EMPTY_LEVELS reason. Reuses the
 * SAME pure `validateGroupedApply` the apply path runs, so the previewed count
 * is exactly what Apply will change. (`showBottomSheet` interpolates the footer
 * once, so we querySelector + rewrite the button in place.)
 */
function refreshFooter(): void {
  if (!_handle) return;
  const res = validateGroupedApply(_slots, buildPatch());
  const emptyCount = res.offending.filter((o) => o.reason === 'EMPTY_LEVELS').length;

  const warnBox = _handle.el.querySelector<HTMLElement>('.gse-levels-warn');
  if (warnBox) {
    if (emptyCount > 0) {
      const noun = emptyCount === 1 ? 'משבצת' : 'משבצות';
      warnBox.textContent = `⚠ כל משבצת חייבת דרגה אחת לפחות — השינוי ירוקן ${emptyCount} ${noun}`;
      warnBox.hidden = false;
    } else {
      warnBox.hidden = true;
      warnBox.textContent = '';
    }
  }

  const applyBtn = _handle.el.querySelector<HTMLButtonElement>('[data-gse="apply"]');
  if (!applyBtn) return;
  if (!res.ok) {
    // EMPTY_LEVELS is the only reachable conflict (cert overlap is structurally
    // impossible from a single tri-state control); keep a generic fallback.
    applyBtn.textContent = emptyCount > 0 ? 'דרגה אחת חובה' : 'לא ניתן להחיל';
    applyBtn.disabled = true;
  } else if (res.changed === 0) {
    applyBtn.textContent = 'אין שינוי';
    applyBtn.disabled = true;
  } else {
    applyBtn.textContent = `החל · ישתנו ${res.changed} מתוך ${_slots.length}`;
    applyBtn.disabled = false;
  }
}

// ─── Apply ────────────────────────────────────────────────────────────────────

function renderConflicts(offending: GroupedConflict[]): string {
  const empty = offending.filter((o) => o.reason === 'EMPTY_LEVELS').map((o) => escHtml(o.label));
  const overlap = offending.filter((o) => o.reason === 'CERT_OVERLAP');
  const lines: string[] = ['<strong>לא ניתן להחיל את השינוי:</strong>'];
  if (empty.length) {
    lines.push(`• המשבצות הבאות יישארו ללא אף דרגה: ${empty.join(', ')}`);
  }
  if (overlap.length) {
    const certNames = (o: GroupedConflict) => (o.certs ?? []).map((c) => escHtml(store.getCertLabel(c))).join(', ');
    const parts = overlap.map((o) => `${escHtml(o.label)} (${certNames(o)})`);
    lines.push(`• הסמכה לא יכולה להיות גם נדרשת וגם אסורה במשבצות: ${parts.join(', ')}`);
  }
  return lines.join('<br>');
}

function onApply(): void {
  if (!_scope || !_handle) return;
  const patch = buildPatch();
  const result = store.applyGroupedSlotEdit(_scope, patch);

  if (!result.ok) {
    // Unreachable in practice (footer gate blocks EMPTY_LEVELS; cert overlap is
    // structurally impossible) — kept as a defensive inline backstop. Per the
    // modal-error rule this surfaces inline, never as a toast.
    const box = _handle.el.querySelector<HTMLElement>('#gse-error');
    if (box) {
      box.innerHTML = renderConflicts(result.offending);
      box.hidden = false;
    }
    return;
  }

  if (result.changed === 0) {
    closeGroupedSlotEdit();
    return;
  }

  closeGroupedSlotEdit();
  showToast(`${result.changed} משבצות עודכנו בהצלחה`, { type: 'success' });
  _ctx?.rerender();
}

// ─── Public API ─────────────────────────────────────────────────────────────

export function openGroupedSlotEdit(scope: GroupedSlotScope): void {
  const view = store.getGroupedSlotScopeView(scope);
  if (!view || view.slots.length === 0) {
    showToast('אין משבצות לעריכה בטווח זה', { type: 'info' });
    return;
  }

  _scope = scope;
  _slots = view.slots;
  const slots = view.slots;

  _levelState.clear();
  _levelOrig.clear();
  for (const l of LEVEL_OPTIONS) {
    const agg = computeLevelAggregate(slots, l);
    _levelState.set(l, agg);
    _levelOrig.set(l, agg);
  }
  _certState.clear();
  _certOrig.clear();
  for (const c of store.getCertificationDefinitions()) {
    const tri = certTriFromAggregates(
      computeCertAggregate(slots, c.id, 'required'),
      computeCertAggregate(slots, c.id, 'forbidden'),
    );
    _certState.set(c.id, tri);
    _certOrig.set(c.id, tri);
  }

  const title = view.subTeamName
    ? `עריכה קבוצתית — ${view.ownerName} › ${view.subTeamName}`
    : `עריכה קבוצתית — ${view.ownerName}`;

  _handle = showBottomSheet(sheetBody(slots.length), {
    title,
    actions: actionsHtml(),
    onClose: () => {
      _scope = null;
      _handle = null;
      _slots = [];
    },
  });

  _handle.el.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;

    // Per-row revert: restore a changed row to its on-open value (the only way
    // back to a mixed/blank row once a value was picked).
    const revertBtn = target.closest<HTMLElement>('[data-gse-revert]');
    if (revertBtn) {
      const row = revertBtn.closest<HTMLElement>('.gse-seg-row');
      if (row?.dataset.gseLvlRow != null) {
        const lvl = Number(row.dataset.gseLvlRow) as Level;
        _levelState.set(lvl, _levelOrig.get(lvl) ?? 'mixed');
        row.outerHTML = levelRowHtml(lvl);
      } else if (row?.dataset.gseCertRow != null) {
        const id = row.dataset.gseCertRow;
        _certState.set(id, _certOrig.get(id) ?? 'leave');
        const def = store.getCertificationById(id);
        row.outerHTML = certRowHtml(id, def?.label ?? id, def?.color ?? '#7f8c8d');
      }
      refreshFooter();
      return;
    }

    const seg = target.closest<HTMLElement>('[data-gse-seg]');
    if (seg) {
      const row = seg.closest<HTMLElement>('.gse-seg-row');
      const kind = seg.dataset.kind;
      const id = seg.dataset.id ?? '';
      const val = seg.dataset.val ?? '';

      if (kind === 'lvl') {
        const lvl = Number(id) as Level;
        if (_levelState.get(lvl) === val) return; // tap on the active segment = no-op
        _levelState.set(lvl, val as LevelCtl);
        if (row) row.outerHTML = levelRowHtml(lvl);
      } else if (kind === 'cert') {
        if (_certState.get(id) === val) return;
        _certState.set(id, val as CertTri);
        const def = store.getCertificationById(id);
        if (row) row.outerHTML = certRowHtml(id, def?.label ?? id, def?.color ?? '#7f8c8d');
      }
      refreshFooter();
      return;
    }

    const act = target.closest<HTMLElement>('[data-gse]')?.dataset.gse;
    if (act === 'apply') onApply();
    else if (act === 'cancel') closeGroupedSlotEdit();
  });

  refreshFooter();
}

export function closeGroupedSlotEdit(): void {
  _handle?.close();
  _handle = null;
  _scope = null;
  _slots = [];
}
